type ToastAppearance = 'success' | 'error' | 'info' | 'warning';

export type ToastContent =
  | string
  | {
      title?: string;
      message?: string;
      link?: string;
      ctaLabel?: string;
    }
  | any;

type ToastConfig = {
  appearance: ToastAppearance;
  autoDismiss: boolean;
  onDismiss?: () => void;
};

const visibleToastKeys = new Set<string>();

const buildToastKey = (
  message: ToastContent,
  appearance: ToastAppearance,
): string => {
  if (typeof message === 'string') {
    return `${appearance}|${message}`;
  }

  if (message && typeof message === 'object') {
    const title = typeof message.title === 'string' ? message.title : '';
    const text = typeof message.message === 'string' ? message.message : '';
    const link = typeof message.link === 'string' ? message.link : '';
    const ctaLabel =
      typeof message.ctaLabel === 'string' ? message.ctaLabel : '';

    return `${appearance}|${title}|${text}|${link}|${ctaLabel}`;
  }

  return `${appearance}|${String(message)}`;
};

export const toast = (message: ToastContent, config: ToastConfig): void => {
  const toastKey = buildToastKey(message, config.appearance);
  if (visibleToastKeys.has(toastKey)) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { ReactToastProviderRef } = window;
  if (!ReactToastProviderRef) {
    // eslint-disable-next-line no-console
    console.error('Could not get react-toast ref');
    return;
  }
  const {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    current: { add },
  } = ReactToastProviderRef;

  const onDismiss = () => {
    visibleToastKeys.delete(toastKey);
    config.onDismiss?.();
  };

  visibleToastKeys.add(toastKey);

  try {
    add(message, {
      ...config,
      onDismiss,
    });
  } catch (error) {
    visibleToastKeys.delete(toastKey);
    throw error;
  }
};
